import {
  FEATURE_FLAGS,
  getFeatureFlag,
} from 'app/config/environment/feature-flag';

export interface IBrand {
  _id: string;
  name: string;
  logoUrl?: string;
  poweredByMaxhomeLogoUrl?: string;
}

const BRANDS_KEY = 'availableBrands';
const ACTIVE_BRAND_KEY = 'activeBrandId';

export const ACTIVE_BRAND_HEADER = 'X-Active-Brand-Id';

// localStorage throws rather than returning null in a private window or when
// site data is blocked. Brand scope is not worth taking the app down for, so
// reads degrade to "nothing stored" and writes report whether they landed.
const readKey = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeKey = (key: string, value: string | null): boolean => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

// The switcher and the logo dereference _id and name without checking, so a
// half-written entry is treated as no entry rather than crashing a render.
const isBrand = (value: unknown): value is IBrand =>
  !!value &&
  typeof value === 'object' &&
  typeof (value as IBrand)._id === 'string' &&
  typeof (value as IBrand).name === 'string';

// Gated at the two readers every consumer goes through (switcher, logo, email
// preview, request header), so a selection stored before the flag went off
// cannot leak through.
const brandsEnabled = () => getFeatureFlag(FEATURE_FLAGS.BRAND_ENABLED);

export const setAvailableBrands = (brands: IBrand[]): boolean =>
  writeKey(BRANDS_KEY, JSON.stringify(brands ?? []));

export const getAvailableBrands = (): IBrand[] => {
  if (!brandsEnabled()) return [];
  const brands = readKey(BRANDS_KEY);
  if (!brands) return [];
  try {
    const parsed = JSON.parse(brands);
    return Array.isArray(parsed) ? parsed.filter(isBrand) : [];
  } catch {
    return [];
  }
};

export const setActiveBrandId = (brandId: string | null): boolean =>
  writeKey(ACTIVE_BRAND_KEY, brandId || null);

export const getActiveBrandId = (): string | null =>
  brandsEnabled() ? readKey(ACTIVE_BRAND_KEY) : null;

export const getActiveBrand = (): IBrand | null => {
  const activeBrandId = getActiveBrandId();
  if (!activeBrandId) return null;
  return (
    getAvailableBrands().find((brand) => brand._id === activeBrandId) ?? null
  );
};

// Profile is how the client learns which brands it still holds, and the server
// rejects an unknown brand id outright — so sending the header on that request
// would make a revoked selection unrecoverable without clearing site data.
const isProfileBootstrap = (url?: string) =>
  Boolean(url?.includes('/user/profile'));

export const applyActiveBrandHeader = (config: {
  url?: string;
  headers: Record<string, string>;
}) => {
  if (isProfileBootstrap(config?.url)) return;
  // The auth token is Firebase-issued, so the active brand cannot ride in it.
  // The server re-validates this against the user's grants on every request.
  const activeBrandId = getActiveBrandId();
  if (activeBrandId) {
    config.headers[ACTIVE_BRAND_HEADER] = activeBrandId;
  }
};

// Called on login: keeps the stored selection only if it is still granted, so a
// revoked brand cannot linger in a header the server would reject. Returns
// false when storage refused the write, leaving the caller to decide.
export const initializeActiveBrand = (brands: IBrand[]): boolean => {
  const granted = (brands ?? []).filter(isBrand);
  if (!setAvailableBrands(granted)) return false;
  if (!granted.length) return setActiveBrandId(null);

  const stored = getActiveBrandId();
  const stillGranted = granted.some((brand) => brand._id === stored);
  return setActiveBrandId(stillGranted ? stored : granted[0]._id);
};
